/**
 * Settings of a family group that the payers step creates or edits: family
 * name, optional billing email (unified only) and billing type.
 */
import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { Home, Percent } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { ContractFamilyGroupDraft } from "@/types/billing";

type FamilyBillingType = ContractFamilyGroupDraft["billingType"];

interface FamilyGroupSettingsFieldsProps {
  group: ContractFamilyGroupDraft;
  /** Household surname derived from the child's parents; the name placeholder. */
  suggestedFamilyName: string;
  /** Name or billing email changed. */
  onChange: (next: ContractFamilyGroupDraft) => void;
  onBillingTypeChange: (type: FamilyBillingType) => void;
}

export function FamilyGroupSettingsFields({
  group,
  suggestedFamilyName,
  onChange,
  onBillingTypeChange,
}: FamilyGroupSettingsFieldsProps) {
  const { i18n } = useLingui();

  return (
    <>
      {/* Family Name */}
      <div>
        <Label htmlFor="family-name">
          <Trans>Family Name</Trans>
        </Label>
        <Input
          id="family-name"
          value={group.familyName}
          onChange={(e) => {
            const next = {
              ...group,
              familyName: e.target.value,
            };
            onChange(next);
          }}
          placeholder={
            suggestedFamilyName || t(i18n)`Enter family name`
          }
          className="mt-1"
        />
      </div>

      {/* Optional family-level billing email override. Leave
          empty to send invoices to each PARENT member's contact
          email (the normal case); fill it in to bill a single
          different address (e.g. an accountant or shared
          household mailbox). */}
      {group.billingType === "UNIFIED" && (
        <div>
          <Label htmlFor="family-billing-email">
            <Trans>Billing Email (optional)</Trans>
          </Label>
          <Input
            id="family-billing-email"
            type="email"
            value={group.unifiedBillingEmail || ""}
            onChange={(e) => {
              const next = {
                ...group,
                unifiedBillingEmail: e.target.value,
              };
              onChange(next);
            }}
            placeholder={t(i18n)`name@example.com`}
            className="mt-1"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            <Trans>
              Leave empty to send invoices to each parent's contact email. Fill in only to bill a different address (e.g. an accountant).
            </Trans>
          </p>
        </div>
      )}

      {/* Billing Type */}
      <div>
        <Label className="mb-2 block">
          <Trans>Billing Type</Trans>
        </Label>
        <RadioGroup
          value={group.billingType}
          onValueChange={(value) =>
            onBillingTypeChange(value as FamilyBillingType)
          }
          name="family-billing-type"
        >
          <div className="flex items-start space-x-3 mb-3">
            <RadioGroupItem
              value="UNIFIED"
              id="family-type-unified"
              className="mt-1"
            />
            <Label
              htmlFor="family-type-unified"
              className="cursor-pointer"
            >
              <div className="flex items-start gap-2">
                <Home className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <div className="font-medium">
                    <Trans>Unified Invoice</Trans>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <Trans>
                      All family members receive the same invoice, any
                      member can pay
                    </Trans>
                  </div>
                </div>
              </div>
            </Label>
          </div>

          <div className="flex items-start space-x-3">
            <RadioGroupItem
              value="SPLIT"
              id="family-type-split"
              className="mt-1"
            />
            <Label
              htmlFor="family-type-split"
              className="cursor-pointer"
            >
              <div className="flex items-start gap-2">
                <Percent className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  <div className="font-medium">
                    <Trans>Split Billing</Trans>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <Trans>
                      Each member gets a separate invoice for their
                      percentage share
                    </Trans>
                  </div>
                </div>
              </div>
            </Label>
          </div>
        </RadioGroup>

        {group.billingType === "UNIFIED" && (
          <p className="mt-2 text-sm text-muted-foreground">
            <Trans>
              Unified invoices create one family payer automatically.
              You can add members later if the family switches to
              split billing.
            </Trans>
          </p>
        )}
      </div>
    </>
  );
}
